<script setup lang="ts">
import AppTable from "@/components/app-table.vue";
import type { CustomerDTO } from "@/models/dtos/customer-dto";
import { TableColumn } from "@/models/table-column";
import { RouteName } from "@/router";
import { authService } from "@/services/auth-service";
import { customersService } from "@/services/customers-service";
import { notificationsService } from "@/services/notifications-service";
import { isCustomerAdminForCustomer, isOneAdmin } from "@/utils/roles.util";
import { onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const pageLoading = ref(false);
const customerColumns = ref([
  TableColumn.for("customerNumber", "Kundnummer"),
  TableColumn.for("name", "Namn"),
]);
const customers = ref<CustomerDTO[]>([]);

onBeforeMount(async () => {
  try {
    pageLoading.value = true;
    const allCustomers = await customersService.getCustomers();
    // Filter customers based on user role and exclude customer with customer number 100000
    customers.value = allCustomers.filter(
      (customer) =>
        (customer.customerNumber !== "100000" &&
          isOneAdmin(authService.loggedInUser)) ||
        isCustomerAdminForCustomer(
          authService.loggedInUser,
          customer.customerNumber,
        ),
    );
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av kunderna.",
      type: "error",
      error,
    });
  } finally {
    pageLoading.value = false;
  }
});

function onClickRow(customer: CustomerDTO) {
  router.push({
    name: RouteName.Customer,
    params: {
      customerNumber: customer.customerNumber,
    },
  });
}
</script>

<template>
  <section class="section px-0">
    <div class="columns is-justify-content-space-between">
      <div class="column">
        <p class="title is-4">Kunder</p>
      </div>

      <div
        v-if="!pageLoading"
        class="column is-narrow is-flex is-align-items-flex-end"
      >
        <router-link
          v-if="isOneAdmin(authService.loggedInUser)"
          class="button"
          :to="{ name: RouteName.CustomersCreate }"
          >Skapa kund</router-link
        >
      </div>
    </div>

    <app-loader v-if="pageLoading"></app-loader>
  </section>

  <section
    v-if="!pageLoading"
    class="section notification has-background-savoy-blue-light"
  >
    <app-table
      :columns="customerColumns"
      :items="customers"
      :loading="pageLoading"
      @click:row="onClickRow"
    ></app-table>
  </section>
</template>
